var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../services/api-client';
let JvAdminCameras = class JvAdminCameras extends LitElement {
    constructor() {
        super(...arguments);
        this._cameras = [];
        this._sites = [];
        this._loading = true;
        this._showForm = false;
        this._formName = '';
        this._formSiteId = '';
        this._formUrl = '';
        this._formError = '';
        this._loadError = '';
        this._saving = false;
    }
    static { this.styles = css `
    :host { display: block; }
    .form-group { margin-bottom: 16px; }
    .table-cell { padding: 12px 16px; }
    .badge-enabled { background: #E9F3ED; color: #4C9A6A; }
    .badge-disabled { background: #F4EEE7; color: #7A7167; }
  `; }
    connectedCallback() {
        super.connectedCallback();
        void this._load();
    }
    async _load() {
        this._loading = true;
        this._loadError = '';
        try {
            const [cameras, sites] = await Promise.all([
                api.get('/api/admin/cameras'),
                api.get('/api/sites'),
            ]);
            this._cameras = cameras;
            this._sites = sites;
        }
        catch (err) {
            this._loadError = err?.message ?? 'Unable to load cameras. Try again.';
        }
        finally {
            this._loading = false;
        }
    }
    async _handleCreate(e) {
        e.preventDefault();
        if (!this._formName || !this._formSiteId) {
            this._formError = 'Name and site are required.';
            return;
        }
        this._saving = true;
        this._formError = '';
        try {
            await api.post('/api/admin/cameras', {
                name: this._formName,
                siteId: this._formSiteId,
                sourceUrl: this._formUrl || null,
                sourceType: this._formUrl && this._formUrl.startsWith('rtsp://') ? 'Rtsp' : 'DirectPush',
                isEnabled: true,
            });
            this._showForm = false;
            this._formName = '';
            this._formSiteId = '';
            this._formUrl = '';
            await this._load();
        }
        catch (err) {
            this._formError = err?.message ?? 'Failed to create camera.';
        }
        finally {
            this._saving = false;
        }
    }
    async _toggleCamera(camera) {
        try {
            await api.patch(`/api/admin/cameras/${camera.id}`, { isEnabled: !camera.isEnabled });
            camera.isEnabled = !camera.isEnabled;
            this.requestUpdate();
        }
        catch { /* handled */ }
    }
    async _deleteCamera(camera) {
        if (!confirm(`Delete ${camera.name}? This cannot be undone.`))
            return;
        try {
            await api.delete(`/api/admin/cameras/${camera.id}`);
            this._cameras = this._cameras.filter(c => c.id !== camera.id);
        }
        catch (err) {
            this._formError = err?.message ?? 'Failed to delete camera.';
        }
    }
    _back() {
        this.dispatchEvent(new CustomEvent('admin-back', { bubbles: true, composed: true }));
    }
    render() {
        return html `
      <div class="max-w-content mx-auto px-5 sm:px-8 py-7">
        <div class="flex items-center gap-4 mb-6">
          <button @click=${this._back} class="text-sm text-jv-primary font-semibold hover:underline">&larr; Dashboard</button>
          <h1 class="text-2xl font-extrabold text-jv-ink">Cameras</h1>
          <button @click=${() => this._showForm = !this._showForm}
            class="ml-auto jv-btn-primary text-xs px-4 py-2">
            ${this._showForm ? 'Cancel' : '+ Add Camera'}
          </button>
        </div>

        ${this._showForm ? html `
          <div class="jv-card mb-6">
            <div class="p-5">
              <h2 class="font-bold text-jv-ink mb-4">Add Camera</h2>
              ${this._formError ? html `<p class="text-jv-danger text-sm mb-4">${this._formError}</p>` : ''}
              <form @submit=${this._handleCreate}>
                <div class="form-group"><label class="jv-label">Name</label><input class="jv-input" .value=${this._formName} @input=${(e) => this._formName = e.target.value} placeholder="Kindergarten Room 1" /></div>
                <div class="form-group"><label class="jv-label">Site</label>
                  <select class="jv-input" @change=${(e) => this._formSiteId = e.target.value}>
                    <option value="">Select site…</option>
                    ${this._sites.map(s => html `<option value=${s.id}>${s.name}</option>`)}
                  </select>
                </div>
                <div class="form-group"><label class="jv-label">RTSP URL (optional)</label><input class="jv-input" .value=${this._formUrl} @input=${(e) => this._formUrl = e.target.value} placeholder="rtsp://192.168.1.10/stream" /></div>
                <button type="submit" ?disabled=${this._saving} class="jv-btn-primary">${this._saving ? 'Saving…' : 'Create Camera'}</button>
              </form>
            </div>
          </div>
        ` : ''}

        ${this._loading ? html `<p class="text-jv-ink-2 text-sm" role="status">Loading cameras…</p>` : this._loadError ? html `
          <div class="jv-card p-6" role="alert"><p class="text-jv-danger text-sm">${this._loadError}</p><button @click=${this._load} class="jv-btn-primary text-xs mt-4">Retry</button></div>
        ` : html `
          ${this._cameras.length === 0 ? html `
            <div class="jv-card"><div class="p-10 text-center"><p class="font-semibold text-jv-ink">No cameras yet</p><p class="text-sm text-jv-ink-2 mt-1">Add your first camera to get started.</p></div></div>
          ` : html `
            <div class="jv-card">
              <table class="w-full text-sm">
                <thead><tr class="border-b border-jv-border">
                  <th class="text-left table-cell text-xs uppercase text-jv-ink-2 font-semibold">Name</th>
                  <th class="text-left table-cell text-xs uppercase text-jv-ink-2 font-semibold hidden sm:table-cell">Site</th>
                  <th class="text-left table-cell text-xs uppercase text-jv-ink-2 font-semibold hidden md:table-cell">Type</th>
                  <th class="text-center table-cell text-xs uppercase text-jv-ink-2 font-semibold">Status</th>
                  <th class="text-right table-cell text-xs uppercase text-jv-ink-2 font-semibold"></th>
                </tr></thead>
                <tbody>
                  ${this._cameras.map(c => html `
                    <tr class="border-b border-jv-border last:border-0 hover:bg-jv-surface-2">
                      <td class="table-cell font-semibold text-jv-ink">${c.name}</td>
                      <td class="table-cell text-jv-ink-2 hidden sm:table-cell">${c.siteName}</td>
                      <td class="table-cell text-jv-ink-2 font-mono text-xs hidden md:table-cell">${c.sourceType}</td>
                      <td class="table-cell text-center">
                        <button @click=${() => this._toggleCamera(c)}
                          class="inline-flex text-xs font-semibold px-2.5 py-1 rounded-full ${c.isEnabled ? 'badge-enabled' : 'badge-disabled'}">
                          ${c.isEnabled ? 'On' : 'Off'}
                        </button>
                      </td>
                      <td class="table-cell text-right">
                        <button @click=${() => this._deleteCamera(c)} class="text-xs text-jv-danger font-semibold hover:underline">
                          Delete
                        </button>
                      </td>
                    </tr>
                  `)}
                </tbody>
              </table>
            </div>
          `}
        `}
      </div>
    `;
    }
};
__decorate([
    state()
], JvAdminCameras.prototype, "_cameras", void 0);
__decorate([
    state()
], JvAdminCameras.prototype, "_sites", void 0);
__decorate([
    state()
], JvAdminCameras.prototype, "_loading", void 0);
__decorate([
    state()
], JvAdminCameras.prototype, "_showForm", void 0);
__decorate([
    state()
], JvAdminCameras.prototype, "_formName", void 0);
__decorate([
    state()
], JvAdminCameras.prototype, "_formSiteId", void 0);
__decorate([
    state()
], JvAdminCameras.prototype, "_formUrl", void 0);
__decorate([
    state()
], JvAdminCameras.prototype, "_formError", void 0);
__decorate([
    state()
], JvAdminCameras.prototype, "_loadError", void 0);
__decorate([
    state()
], JvAdminCameras.prototype, "_saving", void 0);
JvAdminCameras = __decorate([
    customElement('jv-admin-cameras')
], JvAdminCameras);
export { JvAdminCameras };
